import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages } from "@saleor/intl";
import { findValueInEnum } from "@saleor/misc";
import { StoreTypeEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import React from "react";
import { useApolloClient } from "react-apollo";
import { FormattedMessage, useIntl } from "react-intl";

import { PAGINATE_BY } from "../../config";
import { maybe } from "../../misc";
import { useProductBulkDeleteMutation } from "../../products/mutations";
import { productAddUrl, productUrl } from "../../products/urls";
import {
  CategoryPageTab,
  CategoryUpdatePage,
  FormData
} from "../components/CategoryUpdatePage/CategoryUpdatePage";
import {
  useCategoryBulkDeleteMutation,
  useCategoryCreateDeeplink,
  useCategoryDeleteMutation,
  useCategoryUpdateMutation
} from "../mutations";
import { getCategoryParents, useCategoryDetailsQuery } from "../queries";
import { CategoryBulkDelete } from "../types/CategoryBulkDelete";
import { CategoryDelete } from "../types/CategoryDelete";
import { CategoryUpdate } from "../types/CategoryUpdate";
import {
  categoryAddUrl,
  categoryListUrl,
  categoryUrl,
  CategoryUrlDialog,
  CategoryUrlQueryParams
} from "../urls";

export interface CategoryDetailsProps {
  params: CategoryUrlQueryParams;
  id: string;
}

export function getActiveTab(tabName: string): CategoryPageTab {
  return tabName === CategoryPageTab.products
    ? CategoryPageTab.products
    : CategoryPageTab.categories;
}

export const CategoryDetails: React.FC<CategoryDetailsProps> = ({
  id,
  params
}) => {
  const apolloClient = useApolloClient();
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const [breadcrumbsData, setBreadcrumbsData] = React.useState([]);
  const [loadingBreadcrumbs, setLoadingBreadcrumbs] = React.useState(false);

  const paginationState = createPaginationState(PAGINATE_BY, params);
  const { data, loading, refetch } = useCategoryDetailsQuery({
    displayLoader: true,
    variables: { ...paginationState, id }
  });

  const category = data?.category;

  React.useEffect(() => {
    if (data) {
      if (!loadingBreadcrumbs) {
        setBreadcrumbsData([]);
        setLoadingBreadcrumbs(true);
        handleGetParents(data.category.id);
      }
    }
  }, [data]);

  const handleGetParents = async (id: string) => {
    const response = await handleCallGetParents(id);
    if (response.data.category.parent) {
      setBreadcrumbsData(prevArray => [
        response.data.category.parent,
        ...prevArray
      ]);
      handleGetParents(response.data.category.parent.id);
    } else {
      setLoadingBreadcrumbs(false);
    }
  };

  const handleCallGetParents = async (id: string) =>
    apolloClient.query({
      fetchPolicy: "network-only",
      query: getCategoryParents,
      variables: {
        id
      }
    });

  if (category === null) {
    return <NotFoundPage onBack={() => navigate(categoryListUrl())} />;
  }

  const handleCategoryDelete = (data: CategoryDelete) => {
    if (data.categoryDelete.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Category deleted"
        })
      });
      navigate(categoryListUrl());
    }
  };

  const [deleteCategory, deleteResult] = useCategoryDeleteMutation({
    onCompleted: handleCategoryDelete
  });

  const handleCategoryUpdate = (data: CategoryUpdate) => {
    if (data.categoryUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    } else if (
      data.categoryUpdate.errors.some(e => e.code === "INVALID") &&
      data.categoryUpdate.errors.some(e => e.field === "backgroundImage")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "File not supported"
        })
      });
    } else if (
      data.categoryUpdate.errors.some(e => e.code === "INVALID") &&
      data.categoryUpdate.errors.some(e => e.field === "iconImage")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Header icon resolution should not exceed 500x500 pixels"
        })
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "There's an error!"
        })
      });
    }
  };

  const [updateCategory, updateResult] = useCategoryUpdateMutation({
    onCompleted: handleCategoryUpdate
  });

  const handleBulkCategoryDelete = (data: CategoryBulkDelete) => {
    if (data.categoryBulkDelete.errors.length === 0) {
      closeModal();
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
      reset();
    }
  };

  const [
    categoryBulkDelete,
    categoryBulkDeleteOpts
  ] = useCategoryBulkDeleteMutation({
    onCompleted: handleBulkCategoryDelete
  });

  const [
    productBulkDelete,
    productBulkDeleteOpts
  ] = useProductBulkDeleteMutation({
    onCompleted: data => {
      if (data.productBulkDelete.errors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        refetch();
        reset();
      }
    }
  });

  const [createDeeplink, createDeeplinkOpts] = useCategoryCreateDeeplink({
    onCompleted: data => {
      const errors = data?.categoryGenerateDynamicLink?.productErrors;
      if (errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Category Updated",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Oops, somethin went wrong...",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const changeTab = (tabName: CategoryPageTab) => {
    reset();
    navigate(
      categoryUrl(id, {
        activeTab: tabName
      })
    );
  };

  const [openModal, closeModal] = createDialogActionHandlers<
    CategoryUrlDialog,
    CategoryUrlQueryParams
  >(navigate, params => categoryUrl(id, params), params);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    params.activeTab === CategoryPageTab.categories
      ? maybe(() => data.category.children.pageInfo)
      : maybe(() => data.category.products.pageInfo),
    paginationState,
    params
  );

  const [iconToUpload, setIconToUpload] = React.useState(null);

  const handleUpdate = async (formData: FormData) => {
    const result = await updateCategory({
      variables: {
        id,
        input: {
          backgroundImageAlt: formData.backgroundImageAlt,
          color: formData.color,
          descriptionJson: JSON.stringify(formData.description),
          ...(iconToUpload && { iconImage: iconToUpload }),
          ...(formData.iconImage === null && { iconImage: iconToUpload }),
          name: formData.name,
          seo: {
            description: formData.seoDescription,
            title: formData.seoTitle
          },
          slug: formData.slug,
          smMarketsId: formData.smMarketsId ? formData.smMarketsId : null,
          storeType: maybe(() =>
            findValueInEnum(formData.storeType, StoreTypeEnum)
          )
        }
      }
    });

    return result.data.categoryUpdate.errors;
  };
  const handleSubmit = createMetadataUpdateHandler(
    data?.category,
    handleUpdate,
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables })
  );

  return (
    <>
      <WindowTitle title={maybe(() => data.category.name)} />
      <CategoryUpdatePage
        breadcrumbs={breadcrumbsData}
        changeTab={changeTab}
        currentTab={params.activeTab}
        category={maybe(() => data.category)}
        disabled={loading || createDeeplinkOpts.loading}
        errors={updateResult.data?.categoryUpdate.errors || []}
        id={id}
        isLoadingBreadcrumbs={loadingBreadcrumbs}
        onIconUpload={setIconToUpload}
        onAddCategory={() => navigate(categoryAddUrl(id))}
        onAddProduct={() => navigate(productAddUrl)}
        onBack={() => {
          setBreadcrumbsData([]);
          navigate(
            maybe(() => categoryUrl(data.category.parent.id), categoryListUrl())
          );
        }}
        onCategoryClick={id => () => navigate(categoryUrl(id))}
        onClickBreadcrumb={id => navigate(categoryUrl(id))}
        onDelete={() => openModal("delete")}
        onGenerateDeeplink={id => createDeeplink({ variables: { id } })}
        onImageDelete={() =>
          updateCategory({
            variables: {
              id,
              input: {
                backgroundImage: null
              }
            }
          })
        }
        onImageUpload={file =>
          updateCategory({
            variables: {
              id,
              input: {
                backgroundImage: file
              }
            }
          })
        }
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onProductClick={id => () => navigate(productUrl(id))}
        onSubmit={handleSubmit}
        products={maybe(() =>
          data.category.products.edges.map(edge => edge.node)
        )}
        saveButtonBarState={updateResult.status}
        subcategories={maybe(() =>
          data.category.children.edges.map(edge => edge.node)
        )}
        subcategoryListToolbar={
          <IconButton
            color="primary"
            onClick={() =>
              openModal("delete-categories", {
                ids: listElements
              })
            }
          >
            <DeleteIcon />
          </IconButton>
        }
        productListToolbar={
          <IconButton
            color="primary"
            onClick={() =>
              openModal("delete-products", {
                ids: listElements
              })
            }
          >
            <DeleteIcon />
          </IconButton>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
      />
      <ActionDialog
        confirmButtonState={deleteResult.status}
        onClose={closeModal}
        onConfirm={() => deleteCategory({ variables: { id } })}
        open={params.action === "delete"}
        title={intl.formatMessage({
          defaultMessage: "Delete category",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete {categoryName}?"
            values={{
              categoryName: (
                <strong>{maybe(() => data.category.name, "...")}</strong>
              )
            }}
          />
        </DialogContentText>
        <DialogContentText>
          <FormattedMessage defaultMessage="Remember this will also unpin all products assigned to this category, making them unavailable in storefront." />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={
          params.action === "delete-categories" &&
          maybe(() => params.ids.length > 0)
        }
        confirmButtonState={categoryBulkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          categoryBulkDelete({
            variables: { ids: params.ids }
          }).then(() => refetch())
        }
        title={intl.formatMessage({
          defaultMessage: "Delete categories",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this category?} other{Are you sure you want to delete {displayQuantity} categories?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
        <DialogContentText>
          <FormattedMessage defaultMessage="Remember this will also delete all products assigned to this category." />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "delete-products"}
        confirmButtonState={productBulkDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productBulkDelete({
            variables: { ids: params.ids }
          }).then(() => refetch())
        }
        title={intl.formatMessage({
          defaultMessage: "Delete products",
          description: "dialog title"
        })}
        variant="delete"
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this product?} other{Are you sure you want to delete {displayQuantity} products?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};
export default CategoryDetails;
